import React, { useCallback, useRef, useState } from "react"
import Cropper from "react-easy-crop"
import { IoClose } from "react-icons/io5"
import Webcam from "react-webcam"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { TWebUser } from "@letshang/constants"
import { get } from "lodash-es"

import { ProfilePictureOption } from "@fuse/pages/profilePage/useProfileController"

import IconCamera from "@fuse/assets/images/Profile/img-icon-camera.svg"
import IconImage from "@fuse/assets/images/Profile/img-icon-image.svg"

interface ProfilePicturePopupProps {
  user?: TWebUser
  onCloseDetailRowPopup: () => void
  onUploadProfilePicture: (v: string) => Promise<string>
}

type CroppedArea = {
  width: number
  height: number
  x: number
  y: number
}

const ProfilePicturePopup: React.FC<ProfilePicturePopupProps> = ({
  user,
  onCloseDetailRowPopup,
  onUploadProfilePicture,
}) => {
  const [image, setImage] = useState<string | null>(null)
  const [croppedImage, setCroppedImage] = useState<string | null>(
    get(user, "avatarUrl", ""),
  )
  const [currentView, setCurrentView] = useState("")
  const [cameraMode, setCameraMode] = useState("user")
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] =
    useState<CroppedArea | null>(null)

  const webcamRef = useRef<Webcam>(null)

  // Open camera
  const openCamera = () => {
    setCurrentView("camera")
  }

  // Capture photo
  const capturePhoto = () => {
    const imageSrc = webcamRef.current?.getScreenshot()
    if (imageSrc) {
      setImage(imageSrc) // Set captured image
      setCurrentView("cropper")
    }
  }

  // Close camera
  const closeCamera = () => {
    setCurrentView("")
  }

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader()
      reader.onload = () => {
        setImage(reader.result as string)
        setCurrentView("cropper")
      }
      reader.readAsDataURL(file)
    }
  }

  // Crop completion callback
  const onCropComplete = useCallback(
    (croppedArea: CroppedArea, croppedAreaPixels: CroppedArea) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    [],
  )

  const getCroppedImage = async () => {
    if (image && croppedAreaPixels) {
      try {
        const croppedImage = await cropImage(image, croppedAreaPixels)
        onUploadProfilePicture(croppedImage).then(() => {
          setCroppedImage(croppedImage)
          setCurrentView("")
          onCloseDetailRowPopup()
        })
      } catch (error) {
        console.error("Error cropping image:", error)
      }
    }
  }

  // Crop image using Canvas
  const cropImage = (imageSrc: string, crop: CroppedArea): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.src = imageSrc
      img.onload = () => {
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")
        if (!ctx) return reject("Canvas not supported")

        const scaleX = img.naturalWidth / img.width
        const scaleY = img.naturalHeight / img.height

        canvas.width = crop.width
        canvas.height = crop.height

        ctx.drawImage(
          img,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height,
        )

        canvas.toBlob((blob) => {
          if (blob) {
            const fileUrl = URL.createObjectURL(blob)
            resolve(fileUrl)
          }
        }, "image/jpeg")
      }
      img.onerror = () => reject(new Error("Failed to load image."))
    })
  }

  const CropperView = () => {
    return (
      <div className="flex flex-col w-full blurTypeDark">
        <div className="flex flex-row pb-4 items-center border-b border-white/35 mb-4">
          <p className="text-white text-sm text-center w-full">Crop image</p>
          <IoClose
            className="text-white mr-4 text-2xl"
            onClick={() => setCurrentView("")}
          />
        </div>
        <div className="relative w-full h-64 bg-gray-300">
          <Cropper
            image={image || ""}
            crop={crop}
            zoom={zoom}
            aspect={1} // 1:1 aspect ratio
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="flex flex-row pb-4 items-center border-t border-white/35 mt-4">
          <div className="w-full flex justify-center gap-4 mt-4 px-4">
            <button
              onClick={() => setCurrentView("")}
              className="text-white opacity-100 px-5 w-full rounded-full blurTypeLight"
            >
              Cancel
            </button>

            <button
              onClick={getCroppedImage}
              className="text-white opacity-100 px-5 w-full rounded-full blurTypeLight"
            >
              Crop
            </button>
          </div>
        </div>
      </div>
    )
  }

  const ProfilePictureOptionsView = () => {
    return (
      <div className="flex items-center flex-col">
        <div className="relative w-24 h-24 mb-4">
          <img
            // src={"https://picsum.photos/200/200"}
            src={croppedImage}
            alt="profile"
            className="rounded-full w-full h-full object-cover"
          />
        </div>

        <div className="flex flex-col w-full">
          <div className="flex h-[0.5px] w-full bg-white/35"></div>
          <div className="flex flex-row w-full px-4 py-3 gap-2 items-center justify-start cursor-pointer">
            <label
              htmlFor="upload" // Links to input by 'id'
              className="flex flex-row items-center gap-2 cursor-pointer w-full"
            >
              <BlurWrapper
                overlayOpacityDefault="15"
                className="p-1.5 rounded-md"
              >
                <img src={IconImage} />
              </BlurWrapper>
              <p className="text-white text-sm">Upload photo</p>
            </label>

            <input
              id="upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          <div className="flex h-[0.5px] w-full bg-white/35"></div>
          <div
            className="flex flex-row w-full px-4 py-3 gap-2 items-center justify-start cursor-pointer"
            onClick={() => openCamera()}
          >
            <BlurWrapper
              overlayOpacityDefault="15"
              className="p-1.5 rounded-md"
            >
              <img src={IconCamera} />
            </BlurWrapper>
            <p className="text-white text-sm">Take photo</p>
          </div>
        </div>
      </div>
    )
  }

  const flipCameraMode = () => {
    if (cameraMode === "user") {
      setCameraMode("environment")
    } else {
      setCameraMode("user")
    }
  }

  const CameraView = () => {
    return (
      <div className="flex flex-col w-full blurTypeDark">
        <div className="flex flex-row pb-4 items-center border-b border-white/35 mb-4">
          <p className="text-white text-sm text-center w-full">Take photo</p>
          <IoClose className="text-white mr-4 text-2xl" onClick={closeCamera} />
        </div>
        <div className="relative w-full h-64 bg-gray-800">
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            className="w-full h-full rounded-md"
            videoConstraints={{ facingMode: cameraMode }}
          />
        </div>
        <div className="flex flex-row pb-4 items-center border-t border-white/35 mt-4">
          <div className="w-full flex justify-center gap-4 mt-4 px-4">
            <button
              onClick={flipCameraMode}
              className="text-white opacity-100 px-5 w-full rounded-full blurTypeLight"
            >
              Flip camera
            </button>
            <button
              onClick={capturePhoto}
              className="text-white opacity-100 px-5 w-full rounded-full blurTypeLight"
            >
              Capture
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderPopupBody = () => {
    switch (currentView) {
      case "cropper":
        return CropperView()

      case "camera":
        return CameraView()

      default:
        return ProfilePictureOptionsView()
    }
  }

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen w-full"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCloseDetailRowPopup()
        }
      }}
    >
      <div className="w-[90%] max-w-sm rounded-2xl py-4 blurTypeDark">
        {renderPopupBody()}
      </div>
    </div>
  )
}

export default ProfilePicturePopup
